import { create } from 'zustand';
import { TwoFactorMethod } from '@/types/misc';
import { BorrowerUserSignupData, InvestorUserSignupData, UserRole } from '@/types/user';
import { DEFAULT_COUNTRY_CODE } from '@/utils/consts';
import { BorrowerFormData } from '@/utils/formValidation/borrowerForm';
import { InvestorFormData } from '@/utils/formValidation/investorForm';
import { getPhoneWithCountryCode } from '@/utils/misc';
import { UserFormTypeRole } from '@/utils/user';

type SignupStore = {
  // State
  role: UserFormTypeRole | null;
  verificationCode: string;
  verificationErrors: string[];
  showVerificationErrors: boolean;
  twofaMethod: TwoFactorMethod;

  // Borrower form state
  borrowerForm: {
    formData: BorrowerFormData;
    errors: Record<string, string[]>;
    submitAttempted: boolean;
  };

  // Investor form state
  investorForm: {
    formData: InvestorFormData;
    errors: Record<string, string[]>;
    submitAttempted: boolean;
  };

  // Actions
  setRole: (role: UserFormTypeRole) => void;
  updateVerificationCode: (code: string) => void;
  setVerificationErrors: (errors: string[]) => void;
  setShowVerificationErrors: (show: boolean) => void;
  setTwofaMethod: (method: TwoFactorMethod) => void;

  // Borrower form actions
  updateBorrowerFormData: (updates: Partial<BorrowerFormData>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateBorrowerFormField: (field: keyof BorrowerFormData, value: any) => void;
  setBorrowerErrors: (errors: Record<string, string[]>) => void;
  setBorrowerSubmitAttempted: (attempted: boolean) => void;

  // Investor form actions
  updateInvestorFormData: (updates: Partial<InvestorFormData>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateInvestorFormField: (field: keyof InvestorFormData, value: any) => void;
  setInvestorErrors: (errors: Record<string, string[]>) => void;
  setInvestorSubmitAttempted: (attempted: boolean) => void;
  reset: () => void;
};

const initialBorrowerFormData: BorrowerFormData = {
  firstName: '',
  middleName: '',
  lastname: '',
  dateOfBirth: '',
  email: '',
  phone: '',
  countryCode: DEFAULT_COUNTRY_CODE,
  password: '',
  repeatPassword: '',
  acceptedTerms: false,
};

const initialInvestorFormData: InvestorFormData = {
  firstName: '',
  middleName: '',
  lastname: '',
  dateOfBirth: '',
  email: '',
  phone: '',
  countryCode: DEFAULT_COUNTRY_CODE,
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'United States',
  password: '',
  repeat: '',
  ein: '',
  companyName: '',
  acceptedTerms: false,
  user_role: UserRole.INVESTOR_INDIVIDUAL,
};

export const useSignupStore = create<SignupStore>((set) => ({
  // Initial state
  role: null,
  verificationCode: '',
  verificationErrors: [],
  showVerificationErrors: false,
  twofaMethod: TwoFactorMethod.EMAIL,
  borrowerForm: {
    formData: initialBorrowerFormData,
    errors: {},
    submitAttempted: false,
  },
  investorForm: {
    formData: initialInvestorFormData,
    errors: {},
    submitAttempted: false,
  },

  // Actions
  setRole: (role) => {
    set({
      role,
      verificationCode: '',
      verificationErrors: [],
      showVerificationErrors: false,
      borrowerForm: {
        formData: initialBorrowerFormData,
        errors: {},
        submitAttempted: false,
      },
      investorForm: {
        formData: initialInvestorFormData,
        errors: {},
        submitAttempted: false,
      },
    });
  },

  updateVerificationCode: (code) => {
    set({ verificationCode: code.replace(/[^0-9]/g, '').slice(0, 6) });
  },

  setVerificationErrors: (errors) => {
    set({ verificationErrors: errors, showVerificationErrors: errors.length > 0 });
  },

  setShowVerificationErrors: (show) => {
    set({ showVerificationErrors: show });
  },

  setTwofaMethod: (method) => {
    set({ twofaMethod: method });
  },

  // Borrower form actions
  updateBorrowerFormData: (updates) => {
    set((state) => ({
      borrowerForm: {
        ...state.borrowerForm,
        formData: { ...state.borrowerForm.formData, ...updates },
      },
    }));
  },

  updateBorrowerFormField: (field, value) => {
    set((state) => {
      // Clear error for this specific field when user changes it
      const currentErrors = { ...state.borrowerForm.errors };
      delete currentErrors[field];

      return {
        borrowerForm: {
          ...state.borrowerForm,
          formData: { ...state.borrowerForm.formData, [field]: value },
          errors: currentErrors,
        },
      };
    });
  },

  setBorrowerErrors: (errors) => {
    set((state) => ({
      borrowerForm: {
        ...state.borrowerForm,
        errors,
      },
    }));
  },

  setBorrowerSubmitAttempted: (attempted) => {
    set((state) => ({
      borrowerForm: {
        ...state.borrowerForm,
        submitAttempted: attempted,
      },
    }));
  },

  // Investor form actions
  updateInvestorFormData: (updates) => {
    set((state) => ({
      investorForm: {
        ...state.investorForm,
        formData: { ...state.investorForm.formData, ...updates },
      },
    }));
  },

  updateInvestorFormField: (field, value) => {
    set((state) => {
      // Clear error for this specific field when user changes it
      const currentErrors = { ...state.investorForm.errors };
      delete currentErrors[field];

      return {
        investorForm: {
          ...state.investorForm,
          formData: { ...state.investorForm.formData, [field]: value },
          errors: currentErrors,
        },
      };
    });
  },

  setInvestorErrors: (errors) => {
    set((state) => ({
      investorForm: {
        ...state.investorForm,
        errors,
      },
    }));
  },

  setInvestorSubmitAttempted: (attempted) => {
    set((state) => ({
      investorForm: {
        ...state.investorForm,
        submitAttempted: attempted,
      },
    }));
  },

  reset: () => {
    set({
      role: null,
      verificationCode: '',
      verificationErrors: [],
      showVerificationErrors: false,
      twofaMethod: TwoFactorMethod.EMAIL,
      borrowerForm: {
        formData: initialBorrowerFormData,
        errors: {},
        submitAttempted: false,
      },
      investorForm: {
        formData: initialInvestorFormData,
        errors: {},
        submitAttempted: false,
      },
    });
  },
}));

export const getInvestorSignupData = (formData: InvestorFormData): InvestorUserSignupData => {
  const phoneWithCountryCode = getPhoneWithCountryCode(formData.phone, formData.countryCode);
  const commonData = {
    first_name: formData.firstName,
    last_name: formData.lastname,
    user_role: formData.user_role,
    email: formData.email,
    date_of_birth: formData.dateOfBirth,
    phone: phoneWithCountryCode,
    address1: formData.address1,
    address2: formData.address2 || '',
    city: formData.city,
    state: formData.state,
    zip_code: formData.zip,
    country: formData.country || 'United States',
    password: formData.password,
  };
  if (formData.user_role === UserRole.INVESTOR_INDIVIDUAL) {
    return {
      ...commonData,
      user_role: UserRole.INVESTOR_INDIVIDUAL,
    };
  }

  return {
    ...commonData,
    user_role: UserRole.INVESTOR_COMPANY,
    ein: formData.ein.replace(/\D/g, ''), // Sanitize: remove non-digits
    company_name: formData.companyName,
  };
};

export const getBorrowerSignupData = (formData: BorrowerFormData): BorrowerUserSignupData => {
  const phoneWithCountryCode = getPhoneWithCountryCode(formData.phone, formData.countryCode);
  return {
    first_name: formData.firstName,
    middle_name: formData.middleName || '',
    last_name: formData.lastname,
    email: formData.email,
    phone: phoneWithCountryCode,
    date_of_birth: formData.dateOfBirth,
    password: formData.password,
    user_role: UserRole.BORROWER,
  };
};
